import React, { useState, useEffect, useMemo } from 'react'
import HostNav from '../components/hostComponents/HostNav'
import { useNavigate } from 'react-router-dom'
import { getAllOrders } from '../store/thunks/hostActions'
import { useDispatch, useSelector } from 'react-redux'
import { CircularProgress } from '@mui/material'
import { Link } from 'react-router-dom'
import { isUpcomingOrder } from '../utils/orderStatus'

const normalizeStatus = (status) => String(status || '').trim().toLowerCase()

const isStatus = (status, target) => normalizeStatus(status) === normalizeStatus(target)
const isFinalizedOrder = (order) => !isStatus(order?.status, 'created')

function normalizeAddressInput(raw) {
  if (raw == null) return null
  if (typeof raw === 'object' && !Array.isArray(raw)) {
    if (Object.keys(raw).length === 0) return null
    return raw
  }
  if (typeof raw === 'string') {
    const trimmed = raw.trim()
    if (!trimmed || trimmed === '{}' || trimmed === '[]') return null
    try {
      const parsed = JSON.parse(trimmed)
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) return parsed
    } catch (_) {
      /* plain string */
    }
    return { _plain: trimmed }
  }
  return null
}

function structuredAddressLines(obj) {
  if (!obj || obj._plain) return null
  const { propertyManager, line1, line2, city, state, zipCode, entryInstructions } = obj
  const lines = []
  if (propertyManager != null && String(propertyManager).trim()) lines.push(String(propertyManager).trim())
  const streetParts = [line1, line2]
    .map((x) => (x != null ? String(x).trim() : ''))
    .filter(Boolean)
  if (streetParts.length) lines.push(streetParts.join(', '))
  const cityPart = city != null ? String(city).trim() : ''
  const statePart = state != null ? String(state).trim() : ''
  const zipPart = zipCode != null ? String(zipCode).trim() : ''
  let locality = ''
  if (cityPart || statePart) {
    locality = [cityPart, statePart].filter(Boolean).join(', ')
    if (zipPart) locality = `${locality} ${zipPart}`.trim()
  } else if (zipPart) {
    locality = zipPart
  }
  if (locality) lines.push(locality)
  if (entryInstructions != null && String(entryInstructions).trim()) {
    lines.push(String(entryInstructions).trim())
  }
  return lines.length ? lines : null
}

function deliveryAddressToDisplay(raw) {
  const norm = normalizeAddressInput(raw)
  if (!norm) return null
  if (norm._plain) return { kind: 'text', value: norm._plain }
  const lines = structuredAddressLines(norm)
  if (lines?.length) return { kind: 'lines', lines }
  const parts = [norm.line1, norm.line2, norm.city, norm.state, norm.zipCode].filter((x) => x != null && String(x).trim())
  if (parts.length) return { kind: 'text', value: parts.map((x) => String(x).trim()).join(', ') }
  return null
}

function getOrderDeliveryDisplay(order) {
  const sources = [order.deliveryAddress, order.userInputAddress, order.property?.address]
  for (const raw of sources) {
    const disp = deliveryAddressToDisplay(raw)
    if (disp) return disp
  }
  return { kind: 'text', value: 'N/A' }
}

function formatExpectedDeliveryPacific(dateString) {
  if (!dateString) return 'N/A'
  const date = new Date(dateString)
  if (Number.isNaN(date.getTime())) return 'N/A'
  return date.toLocaleString('en-US', {
    timeZone: 'America/Los_Angeles',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
    timeZoneName: 'short',
  })
}

function OrderTableRow({ order, index }) {
  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed': return 'bg-green-100 text-green-700';
      case 'picked': return 'bg-blue-100 text-blue-700';
      case 'pending': case 'requested': case 'paymentcompleted': return 'bg-amber-100 text-amber-700';
      case 'cancelled': return 'bg-red-100 text-red-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <tr className="group hover:bg-gray-50 transition-all duration-200 border-b border-gray-200">
      <td className="px-6 py-4">
        <span className="font-semibold text-gray-900 text-sm">
          #{order.uniqueId || order._id?.slice(-8) || `ORD-${index + 1}`}
        </span>
      </td>
      <td className="px-6 py-4">
        <p className="text-sm font-medium text-gray-900 truncate max-w-[200px]">
          {order.property?.listingTitle || 'Property'}
        </p>
        <p className="text-xs text-gray-500 truncate max-w-[200px]">
          {order.property?.address?.city || 'N/A'}
        </p>
      </td>
      <td className="px-6 py-4">
        <span className="text-sm text-gray-600">{formatDate(order.createdAt || order.inStockDate)}</span>
      </td>
      <td className="px-6 py-4">
        <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold ${getStatusColor(order.status)}`}>
          {isStatus(order.status, 'picked')
            ? 'Picked Up'
            : isStatus(order.status, 'paymentCompleted')
              ? 'Payment Completed'
              : (order.status || 'Unknown')}
        </span>
      </td>
      <td className="px-6 py-4">
        <span className="text-sm font-bold text-green-600">${order.hostFee?.toFixed(2) || '0.00'}</span>
      </td>
      <td className="px-6 py-4 text-right">
        <span className="text-xs text-gray-500">{order.items?.length || 0} items</span>
      </td>
    </tr>
  );
}

function OrderCard({ order, index }) {
  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed': return 'bg-green-100 text-green-700';
      case 'picked': return 'bg-blue-100 text-blue-700';
      case 'pending': case 'requested': case 'paymentcompleted': return 'bg-amber-100 text-amber-700';
      case 'cancelled': return 'bg-red-100 text-red-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const formatAddress = (address) => {
    if (!address) return 'N/A';
    if (typeof address === 'string') {
      const trimmed = address.trim();
      if (!trimmed || trimmed === '{}' || trimmed === '[]') return 'N/A';
      const parsed = normalizeAddressInput(trimmed);
      if (parsed && !parsed._plain) {
        const lines = structuredAddressLines(parsed);
        if (lines?.length) return lines.join('\n');
        const parts = [parsed.line1, parsed.line2, parsed.city, parsed.state, parsed.zipCode].filter((x) => x != null && String(x).trim());
        if (parts.length) return parts.map((x) => String(x).trim()).join(', ');
      }
      return trimmed;
    }
    const parts = [address.line1, address.line2, address.city, address.state].filter(Boolean);
    return parts.length > 0 ? parts.join(', ') : 'N/A';
  };

  const deliveryDisplay = getOrderDeliveryDisplay(order);

  return (
    <div className="group bg-gray-100 rounded-2xl border border-gray-200 overflow-hidden transition-all duration-200 hover:bg-gray-50">
      <div className="p-5 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-base font-bold text-gray-900">
              Order #{order.uniqueId || order._id?.slice(-8) || `ORD-${index + 1}`}
            </h3>
            <p className="text-xs text-gray-500">{formatDate(order.createdAt || order.inStockDate)}</p>
          </div>
          <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold ${getStatusColor(order.status)}`}>
            {isStatus(order.status, 'picked')
              ? 'Picked Up'
              : isStatus(order.status, 'paymentCompleted')
                ? 'Payment Completed'
                : order.status}
          </span>
        </div>
      </div>

      <div className="p-5">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-3">
            <div>
              <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1.5">Property</h4>
              <div className="bg-white rounded-xl p-3 border border-gray-200">
                <p className="font-semibold text-gray-900 text-sm mb-0.5">{order.property?.listingTitle || 'Property Name'}</p>
                <p className="text-xs text-gray-500">{formatAddress(order.property?.address)}</p>
              </div>
            </div>
            <div>
              <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1.5">Delivery Address</h4>
              <div className="bg-white rounded-xl p-3 border border-gray-200 space-y-1">
                {deliveryDisplay.kind === 'lines' ? (
                  deliveryDisplay.lines.map((line, i) => (
                    <p key={i} className="text-xs text-gray-900 whitespace-pre-wrap break-words">
                      {line}
                    </p>
                  ))
                ) : (
                  <p className="text-xs text-gray-900 whitespace-pre-wrap break-words">{deliveryDisplay.value}</p>
                )}
              </div>
            </div>
          </div>

          <div className="space-y-3">
            <div>
              <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1.5">Expected Delivery</h4>
              <div className="bg-white rounded-xl p-3 border border-gray-200">
                <p className="text-xs text-gray-900">{formatExpectedDeliveryPacific(order.inStockDate)}</p>
              </div>
            </div>
            <div>
              <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1.5">Commission</h4>
              <div className="bg-green-50 rounded-xl p-3 border border-green-200">
                <p className="text-xl font-bold text-green-600">${order.hostFee?.toFixed(2) || '0.00'}</p>
                <p className="text-xs text-green-700 font-medium">Earned</p>
              </div>
            </div>
          </div>
        </div>

        {order.items && order.items.length > 0 && (
          <div className="mt-4">
            <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Order Items</h4>
            <div className="space-y-1.5">
              {order.items.slice(0, 3).map((item, itemIndex) => (
                <div key={itemIndex} className="flex items-center justify-between bg-white rounded-lg p-2.5 border border-gray-200">
                  <div>
                    <p className="text-xs font-semibold text-gray-900">{item.name || 'Item'}</p>
                    <p className="text-xs text-gray-500">Qty: {item.quantity || 1}</p>
                  </div>
                  <p className="text-xs font-bold text-gray-900">${item.price?.toFixed(2) || '0.00'}</p>
                </div>
              ))}
              {order.items.length > 3 && (
                <p className="text-xs text-gray-500 text-center py-1">+{order.items.length - 3} more items</p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function OrderFilters({ search, setSearch, statusFilter, setStatusFilter, viewMode, setViewMode, orders }) {
  const statusOptions = [
    { value: 'all', label: 'All' },
    { value: 'upcoming', label: 'Upcoming' },
    { value: 'pending', label: 'Pending' },
    { value: 'requested', label: 'Requested' },
    { value: 'completed', label: 'Completed' },
    { value: 'picked', label: 'Picked Up' },
    { value: 'cancelled', label: 'Cancelled' }
  ];

  const getStatusCount = (status) => {
    if (!orders) return 0;
    if (status === 'all') return orders.length;
    if (status === 'upcoming') return orders.filter((o) => isUpcomingOrder(o)).length;
    return orders.filter((o) => isStatus(o.status, status)).length;
  };

  const hasActiveFilters = search || statusFilter !== 'upcoming';

  return (
    <div className="bg-gray-100 rounded-2xl border border-gray-200 p-5 mb-6 space-y-4">
      <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
        <div className="flex-1">
          <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Search Orders</label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
              <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <input
              type="text"
              placeholder="Search by ID, property, city..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="block w-full pl-10 pr-10 py-2.5 border border-gray-200 rounded-lg bg-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 text-sm transition-all"
            />
            {search && (
              <button onClick={() => setSearch('')} className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600">
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
        </div>

        <div className="sm:w-auto">
          <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">View</label>
          <div className="flex items-center bg-white rounded-xl p-1 border border-gray-200">
            <button onClick={() => setViewMode('card')} className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${viewMode === 'card' ? 'bg-gray-100 text-gray-900' : 'text-gray-500 hover:text-gray-700'}`}>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6z" /></svg>
            </button>
            <button onClick={() => setViewMode('table')} className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${viewMode === 'table' ? 'bg-gray-100 text-gray-900' : 'text-gray-500 hover:text-gray-700'}`}>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" /></svg>
            </button>
          </div>
        </div>

        {hasActiveFilters && (
          <div className="sm:w-auto">
            <label className="block text-xs font-semibold text-transparent mb-1.5">Clear</label>
            <button onClick={() => { setSearch(''); setStatusFilter('all'); }} className="flex items-center gap-1.5 px-3 py-2.5 text-sm font-medium text-gray-600 hover:text-gray-900 bg-white hover:bg-gray-50 rounded-xl transition-all border border-gray-200">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
              <span>Reset</span>
            </button>
          </div>
        )}
      </div>

      <div className="border-t border-gray-200" />

      <div>
        <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2.5">Filter by Status</label>
        <div className="flex flex-wrap gap-2">
          {statusOptions.map((option) => {
            const count = getStatusCount(option.value);
            const isActive = statusFilter === option.value;
            return (
              <button
                key={option.value}
                onClick={() => setStatusFilter(option.value)}
                className={`inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-sm font-medium transition-all duration-200 border ${
                  isActive ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                }`}
              >
                <span>{option.label}</span>
                <span className={`inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-xs font-bold ${isActive ? 'bg-white/20 text-white' : 'bg-gray-100 text-gray-600'}`}>
                  {count}
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function OrderStats({ orders }) {
  const earnedOrders = orders?.filter((o) => isStatus(o.status, 'completed') || isStatus(o.status, 'paymentCompleted')) || [];

  const stats = {
    total: orders?.length || 0,
    completed: orders?.filter((o) => isStatus(o.status, 'completed')).length || 0,
    upcoming: orders?.filter((o) => isUpcomingOrder(o)).length || 0,
    totalEarnings: earnedOrders.reduce((sum, order) => sum + (Number(order.hostFee) || 0), 0)
  };

  const statCards = [
    { label: 'Total Orders', value: stats.total },
    { label: 'Completed', value: stats.completed },
    { label: 'Upcoming', value: stats.upcoming },
    { label: 'Total Earnings', value: `$${stats.totalEarnings.toFixed(2)}` },
  ];

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
      {statCards.map((stat, i) => (
        <div key={i} className="bg-gray-100 rounded-2xl border border-gray-200 p-5">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">{stat.label}</p>
          <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
        </div>
      ))}
    </div>
  );
}

function Pagination({ currentPage, totalPages, totalItems, pageSize, onPageChange, onPageSizeChange }) {
  const pageStart = (currentPage - 1) * pageSize + 1;
  const pageEnd = Math.min(currentPage * pageSize, totalItems);

  const getVisiblePages = () => {
    const pages = [];
    const maxVisible = 5;
    let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let end = Math.min(totalPages, start + maxVisible - 1);
    if (end - start + 1 < maxVisible) start = Math.max(1, end - maxVisible + 1);
    for (let i = start; i <= end; i++) pages.push(i);
    return pages;
  };

  if (totalItems === 0) return null;

  return (
    <div className="bg-gray-100 rounded-2xl border border-gray-200 p-4 mt-6">
      <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
        <div className="flex items-center space-x-4">
          <p className="text-sm text-gray-600">
            Showing <span className="font-bold text-gray-900">{pageStart}</span> to <span className="font-bold text-gray-900">{pageEnd}</span> of <span className="font-bold text-gray-900">{totalItems}</span> orders
          </p>
          <div className="flex items-center space-x-2">
            <span className="text-sm text-gray-500">Per page:</span>
            <select value={pageSize} onChange={(e) => onPageSizeChange(Number(e.target.value))} className="px-2 py-1 border border-gray-200 rounded-lg bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30">
              {[10, 25, 50].map((size) => (<option key={size} value={size}>{size}</option>))}
            </select>
          </div>
        </div>

        <div className="flex items-center space-x-1">
          <button onClick={() => onPageChange(1)} disabled={currentPage === 1} className="px-2.5 py-1.5 rounded-lg text-sm font-medium text-gray-600 hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7m0 0l7-7m-7 7h18" /></svg>
          </button>
          <button onClick={() => onPageChange(currentPage - 1)} disabled={currentPage === 1} className="px-2.5 py-1.5 rounded-lg text-sm font-medium text-gray-600 hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
          </button>
          {getVisiblePages().map((page) => (
            <button key={page} onClick={() => onPageChange(page)} className={`w-9 h-9 rounded-xl text-sm font-semibold transition-all duration-200 ${page === currentPage ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-white'}`}>
              {page}
            </button>
          ))}
          <button onClick={() => onPageChange(currentPage + 1)} disabled={currentPage === totalPages} className="px-2.5 py-1.5 rounded-lg text-sm font-medium text-gray-600 hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
          </button>
          <button onClick={() => onPageChange(totalPages)} disabled={currentPage === totalPages} className="px-2.5 py-1.5 rounded-lg text-sm font-medium text-gray-600 hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" /></svg>
          </button>
        </div>
      </div>
    </div>
  );
}

const OrdersScreen = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch()

  const { loading, orders, error } = useSelector((state) => state.orders)
  const { hostInfo: { _id: hid } } = useSelector((state) => state.host)

  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('upcoming')
  const [viewMode, setViewMode] = useState('card')
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)

  const finalizedOrders = useMemo(() => (orders || []).filter((order) => isFinalizedOrder(order)), [orders])

  const filteredOrders = useMemo(() => {
    return finalizedOrders?.filter((order) => {
      const matchesSearch = !search ||
        (order.uniqueId && order.uniqueId.toLowerCase().includes(search.toLowerCase())) ||
        (order._id && order._id.toLowerCase().includes(search.toLowerCase())) ||
        (order.status && order.status.toLowerCase().includes(search.toLowerCase())) ||
        (order.property?.address?.line1 && order.property.address.line1.toLowerCase().includes(search.toLowerCase())) ||
        (order.property?.address?.city && order.property.address.city.toLowerCase().includes(search.toLowerCase()))

      const matchesStatus =
        statusFilter === 'all' ||
        (statusFilter === 'upcoming' && isUpcomingOrder(order)) ||
        isStatus(order.status, statusFilter)
      return matchesSearch && matchesStatus
    }) || []
  }, [finalizedOrders, search, statusFilter])

  const totalPages = Math.ceil(filteredOrders.length / pageSize)
  const paginatedOrders = useMemo(() => {
    const start = (currentPage - 1) * pageSize
    return filteredOrders.slice(start, start + pageSize)
  }, [filteredOrders, currentPage, pageSize])

  useEffect(() => { setCurrentPage(1) }, [search, statusFilter, pageSize])

  useEffect(() => {
    if (hid) dispatch(getAllOrders(hid))
  }, [dispatch, hid])

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <CircularProgress size={60} sx={{ color: '#1a1a1a' }} />
          <p className="mt-4 text-gray-500 font-semibold">Loading orders...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-7xl mx-auto px-6 py-8">
        <HostNav hide={true} />

        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900" style={{ letterSpacing: '-0.5px' }}>Orders</h1>
              <p className="text-gray-500 mt-1">Manage and track your property orders</p>
            </div>
            <Link to="/host" className="flex items-center space-x-2 px-5 py-2.5 bg-gray-900 text-white rounded-xl hover:bg-gray-800 transition-all duration-200 font-medium text-sm">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" /></svg>
              <span>Back to Dashboard</span>
            </Link>
          </div>
        </div>

        <OrderStats orders={finalizedOrders} />
        <OrderFilters search={search} setSearch={setSearch} statusFilter={statusFilter} setStatusFilter={setStatusFilter} viewMode={viewMode} setViewMode={setViewMode} orders={finalizedOrders} />

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6">
            <p className="text-red-700 font-medium">Error loading orders: {error}</p>
          </div>
        )}

        {filteredOrders.length === 0 ? (
          <div className="bg-gray-100 rounded-2xl border border-gray-200 p-12 text-center">
            <h3 className="text-xl font-bold text-gray-900 mb-2">
              {search || statusFilter !== 'all' ? 'No Orders Found' : 'No Orders Yet'}
            </h3>
            <p className="text-gray-500 mb-6">
              {search || statusFilter !== 'all' ? 'Try adjusting your search or filter criteria' : 'Orders will appear here when guests make purchases'}
            </p>
            {(search || statusFilter !== 'all') && (
              <button onClick={() => { setSearch(''); setStatusFilter('all'); }} className="bg-gray-900 text-white px-6 py-3 rounded-xl hover:bg-gray-800 transition-all font-medium">
                Clear Filters
              </button>
            )}
          </div>
        ) : viewMode === 'table' ? (
          <div className="bg-gray-100 rounded-2xl border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Order ID</th>
                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Property</th>
                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Date</th>
                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Commission</th>
                    <th className="px-6 py-4 text-right text-xs font-bold text-gray-500 uppercase tracking-wider">Items</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {paginatedOrders.map((order, index) => (
                    <OrderTableRow key={order._id || index} order={order} index={(currentPage - 1) * pageSize + index} />
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            {paginatedOrders.map((order, index) => (
              <OrderCard key={order._id || index} order={order} index={(currentPage - 1) * pageSize + index} />
            ))}
          </div>
        )}

        <Pagination currentPage={currentPage} totalPages={totalPages} totalItems={filteredOrders.length} pageSize={pageSize} onPageChange={setCurrentPage} onPageSizeChange={setPageSize} />
      </div>
    </div>
  )
}

export default OrdersScreen
